import React, { useState, useEffect, useRef } from 'react'
import { fetchWithCsrf } from '../utils/csrf'

interface PinUser {
  id: number
  name: string
  has_pin: boolean
}

interface PinLoginProps {
  onSuccess: (user: { id: number; name: string; email: string }) => void
  onPasswordLogin: () => void
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

const COLORS = [
  'bg-indigo-500', 'bg-purple-500', 'bg-pink-500', 'bg-blue-500',
  'bg-teal-500', 'bg-amber-500', 'bg-rose-500', 'bg-cyan-500',
]

function getColor(id: number): string {
  return COLORS[id % COLORS.length]
}

type Step = 'pick-user' | 'enter-pin' | 'set-pin'

export default function PinLogin({ onSuccess, onPasswordLogin }: PinLoginProps) {
  const [users, setUsers] = useState<PinUser[]>([])
  const [selectedUser, setSelectedUser] = useState<PinUser | null>(null)
  const [step, setStep] = useState<Step>('pick-user')
  const [pin, setPin] = useState(['', '', '', ''])
  const [confirmPin, setConfirmPin] = useState(['', '', '', ''])
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [fetchingUsers, setFetchingUsers] = useState(true)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  const confirmRefs = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    fetch('/api/v1/pin_users')
      .then(res => res.json())
      .then(data => setUsers(data.users))
      .catch(() => setError('Failed to load users'))
      .finally(() => setFetchingUsers(false))
  }, [])

  useEffect(() => {
    if (selectedUser && step === 'enter-pin') {
      inputRefs.current[0]?.focus()
    }
    if (selectedUser && step === 'set-pin') {
      inputRefs.current[0]?.focus()
    }
  }, [selectedUser, step])

  const handleSelectUser = (user: PinUser) => {
    setSelectedUser(user)
    if (user.has_pin) {
      setStep('enter-pin')
    } else {
      setStep('set-pin')
    }
  }

  const handleDigit = (index: number, value: string, isConfirm = false) => {
    if (!/^\d?$/.test(value)) return

    const current = isConfirm ? [...confirmPin] : [...pin]
    current[index] = value
    const setter = isConfirm ? setConfirmPin : setPin
    const refs = isConfirm ? confirmRefs : inputRefs
    setter(current)
    setError('')

    if (value && index < 3) {
      refs.current[index + 1]?.focus()
    }

    if (value && index === 3 && current.every(d => d !== '')) {
      if (step === 'enter-pin') {
        submitPin(current.join(''))
      } else if (step === 'set-pin' && isConfirm) {
        const pinValue = pin.join('')
        const confirmValue = current.join('')
        if (pinValue === confirmValue) {
          submitSetPin(pinValue)
        } else {
          setError('PINs do not match')
          setConfirmPin(['', '', '', ''])
          confirmRefs.current[0]?.focus()
        }
      } else if (step === 'set-pin' && !isConfirm) {
        // First PIN entered, focus confirm
        setTimeout(() => confirmRefs.current[0]?.focus(), 50)
      }
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent, isConfirm = false) => {
    const refs = isConfirm ? confirmRefs : inputRefs
    if (e.key === 'Backspace' && !(isConfirm ? confirmPin : pin)[index] && index > 0) {
      refs.current[index - 1]?.focus()
    }
  }

  const submitPin = async (pinValue: string) => {
    if (!selectedUser) return
    setLoading(true)
    setError('')

    try {
      const response = await fetchWithCsrf('/api/v1/pin_login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin: pinValue, user_id: selectedUser.id }),
      })

      const data = await response.json()

      if (response.ok) {
        onSuccess(data.user)
      } else {
        setError(data.error || 'Invalid PIN')
        setPin(['', '', '', ''])
        inputRefs.current[0]?.focus()
      }
    } catch {
      setError('Something went wrong. Please try again.')
      setPin(['', '', '', ''])
    } finally {
      setLoading(false)
    }
  }

  const submitSetPin = async (pinValue: string) => {
    if (!selectedUser) return
    setLoading(true)
    setError('')

    try {
      // First, log in with password to set the PIN
      // We need the user to verify their identity — use a lightweight
      // endpoint that sets PIN by user_id (since this is a trusted club app)
      const response = await fetchWithCsrf('/api/v1/pin_setup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: selectedUser.id, pin: pinValue, pin_confirmation: pinValue }),
      })

      const data = await response.json()

      if (response.ok) {
        // Now log in with the new PIN
        await submitPin(pinValue)
      } else {
        setError(data.errors?.join(', ') || data.error || 'Failed to set PIN')
        setPin(['', '', '', ''])
        setConfirmPin(['', '', '', ''])
        inputRefs.current[0]?.focus()
      }
    } catch {
      setError('Something went wrong. Please try again.')
      setPin(['', '', '', ''])
      setConfirmPin(['', '', '', ''])
    } finally {
      setLoading(false)
    }
  }

  const handleBack = () => {
    setSelectedUser(null)
    setStep('pick-user')
    setPin(['', '', '', ''])
    setConfirmPin(['', '', '', ''])
    setError('')
  }

  if (fetchingUsers) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl mb-4 mx-auto">
            <svg className="w-7 h-7 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Bookclub</h1>
        </div>

        {step === 'pick-user' ? (
          <>
            <p className="text-center text-gray-600 mb-6">Who are you?</p>

            {users.length === 0 ? (
              <p className="text-center text-gray-500 text-sm">No members found.</p>
            ) : (
              <div className="space-y-3">
                {users.map(user => (
                  <button
                    key={user.id}
                    onClick={() => handleSelectUser(user)}
                    className="w-full flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-200 hover:border-indigo-300 hover:shadow-md transition-all"
                  >
                    <div className={`w-10 h-10 rounded-full ${getColor(user.id)} flex items-center justify-center text-white font-semibold text-sm`}>
                      {getInitials(user.name)}
                    </div>
                    <span className="text-gray-900 font-medium">{user.name}</span>
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <button
              onClick={handleBack}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-6 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>

            <div className="text-center mb-8">
              <div className={`w-16 h-16 rounded-full ${getColor(selectedUser!.id)} flex items-center justify-center text-white font-bold text-xl mx-auto mb-3`}>
                {getInitials(selectedUser!.name)}
              </div>
              <p className="text-gray-900 font-semibold text-lg">{selectedUser!.name}</p>
              <p className="text-gray-500 text-sm mt-1">
                {step === 'enter-pin' ? 'Enter your PIN' : 'Set a 4-digit PIN'}
              </p>
            </div>

            {error && (
              <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm text-center">{error}</p>
              </div>
            )}

            {step === 'set-pin' && (
              <p className="text-xs text-gray-500 text-center mb-4">Choose a PIN</p>
            )}

            <div className="flex justify-center gap-3 mb-4">
              {pin.map((digit, i) => (
                <input
                  key={i}
                  ref={el => { inputRefs.current[i] = el }}
                  type="password"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleDigit(i, e.target.value)}
                  onKeyDown={e => handleKeyDown(i, e)}
                  disabled={loading}
                  className="w-14 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none disabled:opacity-50"
                />
              ))}
            </div>

            {step === 'set-pin' && (
              <>
                <p className="text-xs text-gray-500 text-center mb-4 mt-6">Confirm PIN</p>
                <div className="flex justify-center gap-3 mb-4">
                  {confirmPin.map((digit, i) => (
                    <input
                      key={i}
                      ref={el => { confirmRefs.current[i] = el }}
                      type="password"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={e => handleDigit(i, e.target.value, true)}
                      onKeyDown={e => handleKeyDown(i, e, true)}
                      disabled={loading || pin.some(d => d === '')}
                      className="w-14 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none disabled:opacity-50"
                    />
                  ))}
                </div>
              </>
            )}

            {loading && (
              <div className="flex justify-center mt-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            )}
          </>
        )}

        <div className="mt-8 text-center">
          <button
            onClick={onPasswordLogin}
            className="text-sm text-indigo-600 hover:text-indigo-700 transition-colors"
          >
            Sign in with email & password
          </button>
        </div>
      </div>
    </div>
  )
}
